import { Button, Card, TextField } from '@starter/ui';
import { createFileRoute, Link, redirect, useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { SocialSignIn } from '../components/social-sign-in.tsx';
import { readSaved } from '../funnel/state.ts';
import { authClient } from '../lib/auth.ts';
import { internalPath } from '../lib/redirect.ts';

type Mode = 'sign-in' | 'sign-up';

/**
 * The address the funnel was given, if it was given one.
 *
 * Somebody arriving from `/start` typed it two screens ago, and a form that
 * asks for it again is the same defect as a brand that arrives empty. Read
 * from the browser storage the funnel writes rather than from the URL: an
 * address in a query string is an address in the history, in the referrer, and
 * in the logs of whoever the social buttons hand off to.
 */
function emailFromFunnel(): string | undefined {
  const answer = readSaved()?.answers.email;
  return typeof answer === 'string' && answer ? answer : undefined;
}

function Login() {
  const router = useRouter();
  const navigate = Route.useNavigate();
  const { redirectTo, deleted } = Route.useSearch();
  const [funnelEmail] = useState(emailFromFunnel);
  /*
   * Which form opens is decided by where they are going, not by what is in
   * storage: `/welcome` is the funnel's own destination, and the button that
   * sends people there says «Create an account». Somebody who merely has an
   * abandoned funnel in this browser and came to sign in still gets sign-in.
   */
  const [mode, setMode] = useState<Mode>(redirectTo === '/welcome' ? 'sign-up' : 'sign-in');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setPending(true);

    const form = new FormData(event.currentTarget);
    const email = String(form.get('email') ?? '');
    const password = String(form.get('password') ?? '');
    const name = String(form.get('name') ?? '') || email.split('@')[0] || 'New user';

    if (mode === 'sign-up') {
      // Nobody is signed in by this call: the account exists but cannot be used
      // until the address is confirmed, so the next screen is the inbox one.
      const { error: signUpError } = await authClient.signUp.email({
        email,
        password,
        name,
        callbackURL: `/verify-email?state=done&redirectTo=${encodeURIComponent(redirectTo)}`,
      });
      setPending(false);

      if (signUpError) {
        setError(signUpError.message ?? 'Could not create the account');
        return;
      }

      await navigate({ to: '/verify-email', search: { state: 'sent', email, redirectTo } });
      return;
    }

    const { error: signInError } = await authClient.signIn.email({ email, password });
    setPending(false);

    if (signInError) {
      // An unverified account is not a wrong password, and saying so on this
      // screen would leave the only way forward — a new link — off it.
      if (signInError.code === 'EMAIL_NOT_VERIFIED') {
        await navigate({ to: '/verify-email', search: { state: 'unverified', email, redirectTo } });
        return;
      }
      setError(signInError.message ?? 'Authentication failed');
      return;
    }

    // Navigate before invalidating, not after. Invalidating while still on
    // /login re-runs this route's own beforeLoad, which now finds a session and
    // redirects to the same destination — two navigations to one place, racing
    // each other. Leaving first makes it one.
    await router.navigate({ to: redirectTo });
    await router.invalidate();
  }

  return (
    <div className="mx-auto max-w-sm">
      <Card title={mode === 'sign-in' ? 'Sign in' : 'Create an account'}>
        {deleted ? (
          <p className="mb-4 text-sm text-ink-muted" data-testid="account-deleted">
            Your account has been deleted.
          </p>
        ) : null}

        <form className="flex flex-col gap-4" onSubmit={onSubmit}>
          {mode === 'sign-up' ? <TextField label="Name" name="name" autoComplete="name" /> : null}
          <TextField
            label="Email"
            name="email"
            type="email"
            required
            autoComplete="email"
            defaultValue={funnelEmail}
            data-testid="email"
          />
          <TextField
            label="Password"
            name="password"
            type="password"
            required
            minLength={10}
            autoComplete={mode === 'sign-in' ? 'current-password' : 'new-password'}
            description={mode === 'sign-up' ? 'At least 10 characters.' : undefined}
            data-testid="password"
          />

          {error ? (
            <p className="text-sm text-danger-text" data-testid="auth-error" role="alert">
              {error}
            </p>
          ) : null}

          <Button type="submit" disabled={pending} data-testid="submit">
            {pending ? 'Working…' : mode === 'sign-in' ? 'Sign in' : 'Sign up'}
          </Button>

          <div className="flex items-center justify-between">
            <button
              type="button"
              className="text-sm text-ink-muted underline"
              data-testid="toggle-mode"
              onClick={() => {
                setMode(mode === 'sign-in' ? 'sign-up' : 'sign-in');
                setError(null);
              }}
            >
              {mode === 'sign-in' ? 'Need an account?' : 'Already have an account?'}
            </button>

            {mode === 'sign-in' ? (
              <Link
                to="/forgot-password"
                className="text-sm text-ink-muted underline"
                data-testid="forgot-password"
              >
                Forgot password?
              </Link>
            ) : null}
          </div>
        </form>

        <div className="mt-6">
          <SocialSignIn callbackURL={redirectTo} onError={setError} />
        </div>
      </Card>
    </div>
  );
}

export const Route = createFileRoute('/login')({
  validateSearch: z.object({
    redirectTo: z
      .string()
      .optional()
      .transform((value) => internalPath(value)),
    /** Set by the deletion callback, which has no session left to greet. */
    deleted: z.boolean().optional(),
  }),
  beforeLoad: async ({ search }) => {
    const { data } = await authClient.getSession();
    if (data) throw redirect({ to: search.redirectTo });
  },
  component: Login,
});
